import { useFtpStore } from '../../store/ftpStore';

export const UPLOAD_CHUNK_SIZE = 4 * 1024 * 1024;

export const errText = (e) => String(e?.message || e || 'FTP error');

export function newTransferId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * What a starter copies onto its transfer row from the caller: the folder
 * upload it belongs to (the strip groups by it) and whether it replaces a file.
 */
export function rowMeta(meta) {
  const out = {};
  if (meta?.groupId) {
    out.groupId = meta.groupId;
    out.groupName = meta.groupName || '';
    out.groupPath = meta.groupPath || '';
  }
  if (meta?.replaced) out.replaced = true;
  return out;
}

/** A folder upload pre-creates its rows as "queued"; one the user cancelled
 *  before its turn must not start when its turn comes. */
export function wasCancelledWhileQueued(meta) {
  if (!meta?.transferId) return false;
  const row = useFtpStore.getState().transfers.find((tr) => tr.transferId === meta.transferId);
  return !!row && (row.canceled || row.done);
}

/* ── Path helpers ───────────────────────────────────────────────────────── */

export const isWindowsPath = (p) => /^[a-zA-Z]:[\\/]/.test(p || '') || String(p || '').includes('\\');

export function joinRemote(base, name) {
  const b = String(base || '/').replace(/\/+$/, '');
  return `${b}/${name}`;
}

export function parentRemote(path) {
  const norm = String(path || '/').replace(/\/+$/, '');
  if (!norm) return '/';
  const idx = norm.lastIndexOf('/');
  if (idx <= 0) return '/';
  return norm.slice(0, idx);
}

/**
 * Last path segment of a REMOTE-supplied name. Remote listing entries are
 * attacker-controlled (FTP authenticates no server at all), and this value is
 * about to become a local filesystem path, so anything that looks like a path
 * is reduced to its final component before it can be joined. Rust rejects such
 * names at the listing boundary and containment-checks the destination
 * independently (ftp.rs `sanitize_listing_name` / `contain_download_path`) —
 * this is the third layer, not the control.
 */
export function localBaseName(name) {
  const segments = String(name || '').split(/[\\/]/);
  return segments[segments.length - 1] || '';
}

export function joinLocal(base, name) {
  const b = String(base || '');
  const leaf = localBaseName(name);
  const sep = isWindowsPath(b) ? '\\' : '/';
  if (!b) return leaf;
  if (b.endsWith('/') || b.endsWith('\\')) return b + leaf;
  return b + sep + leaf;
}

export function parentLocal(path) {
  const p = String(path || '');
  const win = isWindowsPath(p);
  const norm = p.replace(/[\\/]+$/, '');
  const idx = Math.max(norm.lastIndexOf('/'), norm.lastIndexOf('\\'));
  if (idx <= 0) return win ? norm || p : '/';
  const parent = norm.slice(0, idx);
  if (/^[a-zA-Z]:$/.test(parent)) return `${parent}\\`;
  return parent || '/';
}

export function baseName(path) {
  const parts = String(path || '').split(/[\\/]/);
  return parts[parts.length - 1] || path;
}

/** mtime is epoch seconds (contract i64); tolerate millis. Today → HH:MM,
 *  otherwise yyyy-mm-dd. */
export function fmtMtime(mtime) {
  if (mtime == null) return '';
  const n = Number(mtime);
  if (!Number.isFinite(n) || n <= 0) return '';
  const ms = n > 1e12 ? n : n * 1000;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  if (d.toDateString() === new Date().toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Base64 without btoa-on-huge-strings: build the binary string over the
 *  Uint8Array in 32 KB strides, one btoa per (≤4 MB) chunk. */
export function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const STRIDE = 0x8000;
  for (let i = 0; i < bytes.length; i += STRIDE) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + STRIDE, bytes.length)));
  }
  return btoa(binary);
}

/** Parse "755" / "drwxr-xr-x" style permission strings to a numeric mode. */
export function parsePermissions(str, isDir) {
  const s = String(str || '').trim();
  if (/^[0-7]{3,4}$/.test(s)) return parseInt(s, 8) & 0o777;
  const m = s.match(/[rwxsStT-]{9}$/);
  if (m) {
    let mode = 0;
    for (let i = 0; i < 9; i++) {
      if (m[0][i] !== '-' && m[0][i] !== 'T' && m[0][i] !== 'S') mode |= 1 << (8 - i);
    }
    return mode;
  }
  return isDir ? 0o755 : 0o644;
}
